import { useState } from 'react'
import {
  Button,
  Caption1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Textarea,
  tokens,
} from '@fluentui/react-components'
import { bucketColors } from '../../theme'

interface DeleteDemandDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  demandRef: string
  demandTitle: string
  onConfirm: (reason: string) => void
  pending?: boolean
}

export function DeleteDemandDialog({
  open,
  onOpenChange,
  demandRef,
  demandTitle,
  onConfirm,
  pending,
}: DeleteDemandDialogProps) {
  const [reason, setReason] = useState('')

  function handleOpenChange(next: boolean) {
    if (!next) setReason('')
    onOpenChange(next)
  }

  const canConfirm = reason.trim().length > 0 && !pending

  function handleConfirm() {
    if (!canConfirm) return
    onConfirm(reason.trim())
  }

  return (
    <Dialog open={open} onOpenChange={(_, d) => handleOpenChange(d.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Delete demand</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
              <Caption1 style={{ color: tokens.colorNeutralForeground2 }}>
                You're about to delete <strong>{demandRef}</strong> &ndash; {demandTitle}.
                It will disappear from every list, kanban, and FA workspace.
                Applications and history are preserved; an admin can restore it later.
              </Caption1>

              <div>
                <Caption1 style={{ display: 'block', marginBottom: 6 }}>
                  Reason <strong style={{ color: bucketColors.highPriority }}>(required)</strong>
                </Caption1>
                <Textarea
                  value={reason}
                  onChange={(_, d) => setReason(d.value)}
                  rows={3}
                  resize="vertical"
                  placeholder="Why is this demand being deleted? Logged in the demand's timeline."
                  aria-label="Reason for deletion"
                  data-testid="delete-demand-reason"
                />
              </div>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={handleConfirm}
              disabled={!canConfirm}
              data-testid="delete-demand-confirm"
              style={{
                background: canConfirm ? bucketColors.highPriority : undefined,
                borderColor: canConfirm ? bucketColors.highPriority : undefined,
              }}
            >
              {pending ? 'Deleting…' : 'Delete demand'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}
